import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";
import { SERVICES_LIST } from "@/data/services";

const EASE = [0.76, 0, 0.24, 1];

const initialForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  service: "",
  message: "",
};

export const ContactForm = () => {
  const [form, setForm] = useState(initialForm);
  const [state, setState] = useState({ loading: false, success: false, error: "" });

  const onChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true, success: false, error: "" });
    try {
      await submitContact(form);
      setState({ loading: false, success: true, error: "" });
      toast.success("Thank you — we'll reach out shortly.");
      setForm(initialForm);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setState({ loading: false, success: false, error: String(msg) });
      toast.error(String(msg));
    }
  };

  return (
    <section className="relative bg-[var(--bg)] py-16 md:py-36 border-t border-[var(--line-strong)]">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-10 md:gap-20">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="md:col-span-5"
        >
          <p className="overline mb-6">Let&apos;s talk</p>
          <h2 className="font-display text-4xl md:text-6xl tracking-[-0.02em] leading-[0.95] text-[var(--ink)]">
            Tell us a little.{" "}
            <span className="font-serif-accent italic text-[var(--accent)]">
              We&apos;ll take it from here.
            </span>
          </h2>
          <p className="mt-8 text-[var(--ink-80)] leading-relaxed max-w-md">
            Share your context in the form and we&apos;ll come back within one
            business day with a proposed shape — scope, timeline and price.
            No cold emails, no spam list.
          </p>

          <div className="mt-14 border-t border-[var(--line-strong)] pt-8 space-y-6">
            <div>
              <p className="overline mb-2">What happens next</p>
              <ol className="space-y-3 text-[var(--ink-80)] text-sm">
                <li className="flex gap-3">
                  <span className="font-serif-accent italic text-[var(--accent)] text-lg leading-none">01</span>
                  <span>We read your brief and match it to the right practice.</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-serif-accent italic text-[var(--accent)] text-lg leading-none">02</span>
                  <span>A 30-minute discovery call at a time that works for you.</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-serif-accent italic text-[var(--accent)] text-lg leading-none">03</span>
                  <span>A written proposal with scope, timeline and price — inside 1 business day.</span>
                </li>
              </ol>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
          className="md:col-span-7 space-y-2"
          data-testid={CONTACT.form}
        >
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="overline block mb-2">Your name</label>
              <input
                required
                className="editorial-input"
                value={form.name}
                onChange={onChange("name")}
                placeholder="Full name"
                data-testid={CONTACT.name}
              />
            </div>
            <div>
              <label className="overline block mb-2">Company</label>
              <input
                className="editorial-input"
                value={form.company}
                onChange={onChange("company")}
                placeholder="Organisation"
                data-testid={CONTACT.company}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 pt-4">
            <div>
              <label className="overline block mb-2">Work email</label>
              <input
                required
                type="email"
                className="editorial-input"
                value={form.email}
                onChange={onChange("email")}
                placeholder="name@company.com"
                data-testid={CONTACT.email}
              />
            </div>
            <div>
              <label className="overline block mb-2">Phone (optional)</label>
              <input
                className="editorial-input"
                value={form.phone}
                onChange={onChange("phone")}
                placeholder="+91 …"
                data-testid={CONTACT.phone}
              />
            </div>
          </div>

          <div className="pt-4">
            <label className="overline block mb-2">Practice of interest</label>
            <select
              value={form.service}
              onChange={onChange("service")}
              className="editorial-input appearance-none cursor-pointer"
              data-testid={CONTACT.service}
            >
              <option value="">Choose one…</option>
              {SERVICES_LIST.map((s) => (
                <option key={s.id} value={s.title}>
                  {s.number} · {s.title}
                </option>
              ))}
              <option value="Not sure yet">Not sure yet — advise me</option>
            </select>
          </div>

          <div className="pt-4">
            <label className="overline block mb-2">What&apos;s the brief?</label>
            <textarea
              required
              rows={4}
              className="editorial-input resize-none"
              value={form.message}
              onChange={onChange("message")}
              placeholder="A few lines about your stage, headcount, timelines…"
              data-testid={CONTACT.message}
            />
          </div>

          <div className="pt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <button
              type="submit"
              disabled={state.loading}
              className="btn-pill disabled:opacity-60"
              data-testid={CONTACT.submit}
            >
              {state.loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Sending…</span>
                </>
              ) : (
                <>
                  <span>Send enquiry</span>
                  <ArrowUpRight className="w-4 h-4" />
                </>
              )}
            </button>
            {state.success && (
              <div
                className="flex items-center gap-2 text-[var(--accent)]"
                data-testid={CONTACT.success}
              >
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm uppercase tracking-[0.22em]">
                  Received — we&apos;ll reply within 1 business day
                </span>
              </div>
            )}
            {state.error && (
              <div className="text-red-600 text-sm" data-testid={CONTACT.error}>
                {state.error}
              </div>
            )}
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default ContactForm;
